import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";

const C = {
  brand: "#1C2127", brandMid: "#252B33", gold: "#C8A96E", goldMuted: "rgba(200,169,110,0.12)",
  goldDark: "#B39555", cream: "#FAF8F5", white: "#FFFFFF",
  g50: "#FAFAF8", g100: "#F0EFEC", g200: "#E3E1DC", g400: "#A8A49C",
  g500: "#7A766E", g600: "#5C5850", g900: "#1A1815",
  green: "#2D8A4E", greenBg: "rgba(45,138,78,0.08)", amber: "#C4872E", amberBg: "rgba(196,135,46,0.08)",
  blue: "#2E6B9C", blueBg: "rgba(46,107,156,0.08)", red: "#B5443A",
};

export default function AdminDashboard() {
  const { api, logout, user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [members, setMembers] = useState([]);
  const [docs, setDocs] = useState([]);
  const [activity, setActivity] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", firstName: "", lastName: "", fund: "" });
  const [upForm, setUpForm] = useState({ investorId: "", docType: "Financial Statement", fund: "", description: "" });
  const [msg, setMsg] = useState("");
  const fileRef = useRef();

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [m, d, a] = await Promise.all([api("/api/members"), api("/api/documents"), api("/api/documents/activity")]);
      setMembers(m); setDocs(d); setActivity(a);
    } catch (e) { console.error(e); }
  };

  const addMember = async (e) => {
    e.preventDefault();
    try {
      await api("/api/members", { method: "POST", body: JSON.stringify(form) });
      setShowAdd(false); setForm({ email: "", password: "", firstName: "", lastName: "", fund: "" });
      setMsg("Member created"); load();
    } catch (e) { setMsg(e.message); }
  };

  const uploadDoc = async (e) => {
    e.preventDefault();
    if (!fileRef.current?.files[0]) return setMsg("Select a file");
    const fd = new FormData();
    fd.append("file", fileRef.current.files[0]);
    fd.append("investorId", upForm.investorId);
    fd.append("docType", upForm.docType);
    fd.append("fund", upForm.fund);
    fd.append("description", upForm.description);
    try {
      await api("/api/documents/upload", { method: "POST", body: fd });
      setShowUpload(false); setUpForm({ investorId: "", docType: "Financial Statement", fund: "", description: "" });
      setMsg("Document uploaded"); load();
    } catch (e) { setMsg(e.message); }
  };

  const deleteMember = async (id) => {
    if (!window.confirm("Delete this member and all their documents?")) return;
    await api(`/api/members/${id}`, { method: "DELETE" }); load();
  };

  const resetPassword = async (m) => {
    const pw = window.prompt(`Set a new password for ${m.firstName} ${m.lastName} (at least 8 characters):`);
    if (!pw) return;
    try {
      await api(`/api/members/${m.id}/reset-password`, { method: "POST", body: JSON.stringify({ newPassword: pw }) });
      setMsg(`Password reset for ${m.firstName}. Share it with them securely.`);
      load();
    } catch (e) { setMsg(e.message); }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api(`/api/documents/${id}`, { method: "DELETE" }); load();
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Never";
  const fmtSize = b => b > 1048576 ? `${(b/1048576).toFixed(1)} MB` : `${Math.round(b/1024)} KB`;

  const navItems = [
    { id: "dashboard", label: "Dashboard" },
    { id: "members", label: "Members" },
    { id: "documents", label: "Documents" },
    { id: "activity", label: "Activity Log" },
  ];

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: C.g50 }}>
      {/* Sidebar */}
      <aside style={{ width: 240, background: C.brand, display: "flex", flexDirection: "column", justifyContent: "space-between", position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 10 }}>
        <div style={{ padding: "24px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 36, paddingLeft: 4 }}>
            <div style={{ width: 36, height: 36, border: `1.5px solid ${C.gold}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 18, color: C.gold, fontFamily: "'Playfair Display', Georgia, serif" }}>M</span>
            </div>
            <div>
              <div style={{ fontSize: 15, color: C.white, fontWeight: 500, fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: 2 }}>MEDINA</div>
              <div style={{ fontSize: 9, color: C.gold, letterSpacing: 3 }}>405 · ADMIN</div>
            </div>
          </div>
          <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {navItems.map(n => (
              <button key={n.id} onClick={() => setTab(n.id)} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 6, border: "none",
                background: tab === n.id ? "rgba(255,255,255,0.08)" : "transparent",
                color: tab === n.id ? C.white : "rgba(255,255,255,0.55)",
                fontSize: 13.5, fontWeight: 500, width: "100%", textAlign: "left", fontFamily: "'DM Sans', sans-serif",
              }}>{n.label}</button>
            ))}
          </nav>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ height: 1, background: "rgba(255,255,255,0.06)", marginBottom: 12 }} />
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", marginBottom: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: 6, background: C.gold, color: C.brand, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600 }}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: C.white }}>{user?.firstName} {user?.lastName}</div>
              <div style={{ fontSize: 10, color: C.gold, letterSpacing: 1 }}>ADMIN</div>
            </div>
          </div>
          <button onClick={logout} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 6, border: "none", background: "rgba(255,255,255,0.03)", color: "rgba(255,255,255,0.35)", fontSize: 12.5, width: "100%", textAlign: "left", fontFamily: "'DM Sans', sans-serif" }}>Sign Out</button>
        </div>
      </aside>

      <main style={{ flex: 1, marginLeft: 240 }}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "24px 40px", borderBottom: `1px solid ${C.g100}`, background: C.white, position: "sticky", top: 0, zIndex: 5 }}>
          <h1 style={{ fontSize: 22, fontWeight: 400, color: C.g900, fontFamily: "'Playfair Display', Georgia, serif" }}>
            {tab === "dashboard" ? "Admin Dashboard" : tab === "members" ? "Member Management" : tab === "documents" ? "Document Management" : "Activity Log"}
          </h1>
          {msg && <div style={{ padding: "6px 16px", background: C.greenBg, color: C.green, borderRadius: 6, fontSize: 13, cursor: "pointer" }} onClick={() => setMsg("")}>{msg} ×</div>}
        </header>

        <div style={{ padding: "32px 40px" }}>
          {/* DASHBOARD */}
          {tab === "dashboard" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div style={{ background: `linear-gradient(135deg, ${C.brand} 0%, ${C.brandMid} 100%)`, borderRadius: 8, padding: 28, color: C.white }}>
                <div style={{ fontSize: 20, fontWeight: 400, marginBottom: 8, fontFamily: "'Playfair Display', Georgia, serif" }}>Welcome, {user?.firstName}</div>
                <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }}>{members.length} members · {docs.length} documents uploaded</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                {[
                  { label: "Total Members", value: members.length, color: C.blue, bg: C.blueBg },
                  { label: "Documents", value: docs.length, color: C.green, bg: C.greenBg },
                  { label: "Activity Events", value: activity.length, color: C.gold, bg: C.goldMuted },
                ].map((s, i) => (
                  <div key={i} style={{ background: C.white, borderRadius: 8, padding: 20, border: `1px solid ${C.g100}` }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: C.g900 }}>{s.value}</div>
                    <div style={{ fontSize: 13, color: C.g500, marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ background: C.white, borderRadius: 8, border: `1px solid ${C.g100}`, padding: 16 }}>
                  <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Quick Actions</h2>
                  <button onClick={() => { setTab("members"); setShowAdd(true); }} style={{ display: "block", width: "100%", padding: "12px 16px", borderRadius: 6, border: `1px solid ${C.g100}`, background: C.white, textAlign: "left", fontSize: 13.5, fontWeight: 500, color: C.g900, marginBottom: 8, fontFamily: "'DM Sans', sans-serif" }}>+ Add New Member</button>
                  <button onClick={() => { setTab("documents"); setShowUpload(true); }} style={{ display: "block", width: "100%", padding: "12px 16px", borderRadius: 6, border: `1px solid ${C.g100}`, background: C.white, textAlign: "left", fontSize: 13.5, fontWeight: 500, color: C.g900, fontFamily: "'DM Sans', sans-serif" }}>+ Upload Document</button>
                </div>
                <div style={{ background: C.white, borderRadius: 8, border: `1px solid ${C.g100}` }}>
                  <div style={{ padding: "16px", borderBottom: `1px solid ${C.g100}` }}><h2 style={{ fontSize: 15, fontWeight: 600 }}>Recent Activity</h2></div>
                  {activity.slice(0, 4).map(a => (
                    <div key={a.id} style={{ padding: "10px 16px", borderBottom: `1px solid ${C.g100}`, fontSize: 13, color: C.g600 }}>
                      <strong>{a.userName}</strong> — {a.details}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* MEMBERS */}
          {tab === "members" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button onClick={() => setShowAdd(!showAdd)} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: C.brand, color: C.white, fontSize: 13, fontWeight: 500 }}>+ Add Member</button>
              </div>
              {showAdd && (
                <form onSubmit={addMember} style={{ background: C.white, borderRadius: 8, border: `1px solid ${C.g100}`, padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <input placeholder="First Name" value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} required style={{ width: "100%", padding: "10px 12px", border: `1px solid ${C.g200}`, borderRadius: 6, fontSize: 14 }} />
                  <input placeholder="Last Name" value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} required style={{ width: "100%", padding: "10px 12px", border: `1px solid ${C.g200}`, borderRadius: 6, fontSize: 14 }} />
                  <input placeholder="Email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required style={{ width: "100%", padding: "10px 12px", border: `1px solid ${C.g200}`, borderRadius: 6, fontSize: 14 }} />
                  <input placeholder="Temporary Password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required style={{ width: "100%", padding: "10px 12px", border: `1px solid ${C.g200}`, borderRadius: 6, fontSize: 14 }} />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="submit" style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: C.brand, color: C.white, fontSize: 13, fontWeight: 500 }}>Create</button>
                    <button type="button" onClick={() => setShowAdd(false)} style={{ padding: "8px 20px", borderRadius: 6, border: `1px solid ${C.g200}`, background: C.white, color: C.g600, fontSize: 13 }}>Cancel</button>
                  </div>
                </form>
              )}
              <div style={{ background: C.white, borderRadius: 8, border: `1px solid ${C.g100}`, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>{["Name", "Email", "Documents", "Last Login", "Actions"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "12px 22px", fontSize: 11, fontWeight: 600, color: C.g400, textTransform: "uppercase", letterSpacing: 0.8, borderBottom: `1px solid ${C.g100}`, background: C.g50 }}>{h}</th>
                  ))}</tr></thead>
                  <tbody>{members.map(m => (
                    <tr key={m.id} style={{ borderBottom: `1px solid ${C.g100}` }}>
                      <td style={{ padding: "14px 22px", fontSize: 14, fontWeight: 500, color: C.g900 }}>{m.firstName} {m.lastName}</td>
                      <td style={{ padding: "14px 22px", fontSize: 13, color: C.g500 }}>{m.email}</td>
                      <td style={{ padding: "14px 22px", fontSize: 13, fontWeight: 600, color: C.g900 }}>{m.docCount}</td>
                      <td style={{ padding: "14px 22px", fontSize: 13, color: C.g400 }}>{fmtDate(m.lastLogin)}</td>
                      <td style={{ padding: "14px 22px", textAlign: "right" }}>
                        <button onClick={() => { setUpForm({...upForm, investorId: m.id}); setTab("documents"); setShowUpload(true); }} style={{ padding: "5px 12px", borderRadius: 5, border: "none", background: C.brand, color: C.white, fontSize: 12, fontWeight: 500, marginRight: 6 }}>Upload</button>
                        <button onClick={() => resetPassword(m)} style={{ padding: "5px 12px", borderRadius: 5, border: `1px solid ${C.g200}`, background: C.white, color: C.g900, fontSize: 12, marginRight: 6 }}>Reset Password</button>
                        <button onClick={() => deleteMember(m.id)} style={{ padding: "5px 12px", borderRadius: 5, border: `1px solid ${C.g200}`, background: C.white, color: C.red, fontSize: 12 }}>Delete</button>
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* DOCUMENTS */}
          {tab === "documents" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button onClick={() => setShowUpload(!showUpload)} style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: C.brand, color: C.white, fontSize: 13, fontWeight: 500 }}>+ Upload Document</button>
              </div>
              {showUpload && (
                <form onSubmit={uploadDoc} style={{ background: C.white, borderRadius: 8, border: `1px solid ${C.g100}`, padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <select value={upForm.investorId} onChange={e => setUpForm({...upForm, investorId: e.target.value})} required style={{ padding: "10px 12px", border: `1px solid ${C.g200}`, borderRadius: 6, fontSize: 14, background: C.white }}>
                    <option value="">Select Member</option>
                    {members.map(m => <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>)}
                  </select>
                  <select value={upForm.docType} onChange={e => setUpForm({...upForm, docType: e.target.value})} style={{ padding: "10px 12px", border: `1px solid ${C.g200}`, borderRadius: 6, fontSize: 14, background: C.white }}>
                    <option>Financial Statement</option><option>Investor Report</option><option>K-1 Tax Document</option>
                    <option>Capital Call Notice</option><option>Distribution Notice</option><option>Other</option>
                  </select>
                  <input placeholder="Description (optional)" value={upForm.description} onChange={e => setUpForm({...upForm, description: e.target.value})} style={{ width: "100%", padding: "10px 12px", border: `1px solid ${C.g200}`, borderRadius: 6, fontSize: 14 }} />
                  <input type="file" ref={fileRef} accept=".pdf,.xlsx,.xls,.csv,.doc,.docx" required style={{ padding: "8px 0", fontSize: 14 }} />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="submit" style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: C.brand, color: C.white, fontSize: 13, fontWeight: 500 }}>Upload</button>
                    <button type="button" onClick={() => setShowUpload(false)} style={{ padding: "8px 20px", borderRadius: 6, border: `1px solid ${C.g200}`, background: C.white, color: C.g600, fontSize: 13 }}>Cancel</button>
                  </div>
                </form>
              )}
              <div style={{ background: C.white, borderRadius: 8, border: `1px solid ${C.g100}`, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>{["Document", "Type", "Member", "Uploaded", "Size", ""].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "12px 22px", fontSize: 11, fontWeight: 600, color: C.g400, textTransform: "uppercase", letterSpacing: 0.8, borderBottom: `1px solid ${C.g100}`, background: C.g50 }}>{h}</th>
                  ))}</tr></thead>
                  <tbody>{docs.map(d => (
                    <tr key={d.id} style={{ borderBottom: `1px solid ${C.g100}` }}>
                      <td style={{ padding: "14px 22px", fontSize: 13.5, fontWeight: 500, color: C.g900 }}>{d.originalName}</td>
                      <td style={{ padding: "14px 22px" }}><span style={{ fontSize: 11.5, fontWeight: 500, padding: "3px 10px", borderRadius: 20, background: C.blueBg, color: C.blue }}>{d.docType}</span></td>
                      <td style={{ padding: "14px 22px", fontSize: 13, color: C.g500 }}>{d.investorName}</td>
                      <td style={{ padding: "14px 22px", fontSize: 13, color: C.g400 }}>{fmtDate(d.uploadedAt)}</td>
                      <td style={{ padding: "14px 22px", fontSize: 13, color: C.g400 }}>{fmtSize(d.fileSize)}</td>
                      <td style={{ padding: "14px 22px", textAlign: "right" }}>
                        <button onClick={() => deleteDoc(d.id)} style={{ padding: "5px 12px", borderRadius: 5, border: `1px solid ${C.g200}`, background: C.white, color: C.red, fontSize: 12 }}>Delete</button>
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* ACTIVITY */}
          {tab === "activity" && (
            <div style={{ background: C.white, borderRadius: 8, border: `1px solid ${C.g100}`, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>{["User", "Action", "Details", "Date"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "12px 22px", fontSize: 11, fontWeight: 600, color: C.g400, textTransform: "uppercase", letterSpacing: 0.8, borderBottom: `1px solid ${C.g100}`, background: C.g50 }}>{h}</th>
                ))}</tr></thead>
                <tbody>{activity.map(a => (
                  <tr key={a.id} style={{ borderBottom: `1px solid ${C.g100}` }}>
                    <td style={{ padding: "14px 22px", fontSize: 13, fontWeight: 500, color: C.g900 }}>{a.userName}</td>
                    <td style={{ padding: "14px 22px", fontSize: 13, color: C.g500 }}>{a.action}</td>
                    <td style={{ padding: "14px 22px", fontSize: 13, color: C.g500 }}>{a.details}</td>
                    <td style={{ padding: "14px 22px", fontSize: 13, color: C.g400 }}>{fmtDate(a.createdAt)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
